import { useLanguage } from '@/i18n/LanguageContext'
import { useTheme } from '@/contexts/ThemeContext'
import { cn } from '@/lib/utils'
import { Shield } from 'lucide-react'

export function PrivacyPage() {
  const { language } = useLanguage()
  const { theme } = useTheme()

  const content = {
    es: {
      title: 'Política de Privacidad',
      lastUpdate: 'Última actualización: Enero 2025',
      sections: [
        {
          title: '1. Información General',
          content: `W2USDT es un token (contrato inteligente) desplegado en la red blockchain Uni2 (Chain ID: 111888). Este servicio de bridge permite a los usuarios convertir sus tokens W2USDT a USDT en la red Binance Smart Chain (BSC).

Al utilizar este servicio, usted acepta las prácticas descritas en esta política de privacidad.`
        },
        {
          title: '2. Naturaleza del Servicio',
          content: `W2USDT Bridge es un servicio descentralizado que opera mediante contratos inteligentes en la blockchain. El contrato de W2USDT está desplegado en la red Uni2 y su código es público y verificable.

No somos una entidad financiera regulada ni ofrecemos servicios de custodia de activos. Los usuarios mantienen el control total de sus fondos en todo momento hasta que deciden realizar una operación de bridge.`
        },
        {
          title: '3. Datos que Recopilamos',
          content: `Debido a la naturaleza pública de la blockchain, las siguientes informaciones son inherentemente públicas:

• Direcciones de wallet utilizadas para las transacciones
• Montos de las transacciones realizadas
• Historial de transacciones en la blockchain
• Hashes de transacciones (TX Hash)

No recopilamos información personal identificable como nombres, correos electrónicos, números de teléfono o documentos de identidad.`
        },
        {
          title: '4. Uso de la Información',
          content: `La información de las transacciones en blockchain se utiliza exclusivamente para:

• Procesar las solicitudes de bridge
• Verificar la validez de las transacciones
• Mantener un registro de las operaciones realizadas
• Mejorar el funcionamiento del servicio`
        },
        {
          title: '5. Almacenamiento de Datos',
          content: `Las transacciones quedan registradas permanentemente en las blockchains de Uni2 y BSC debido a su naturaleza inmutable. Nuestros sistemas mantienen registros temporales necesarios para el procesamiento de las solicitudes de bridge.`
        },
        {
          title: '6. Seguridad',
          content: `Implementamos medidas de seguridad técnicas para proteger el servicio, incluyendo:

• Contratos inteligentes auditados
• Verificación de transacciones antes del procesamiento
• Monitoreo continuo del sistema

Sin embargo, los usuarios son responsables de la seguridad de sus propias wallets y claves privadas.`
        },
        {
          title: '7. Cookies y Almacenamiento Local',
          content: `Utilizamos almacenamiento local del navegador (localStorage) únicamente para:

• Guardar preferencias de idioma
• Guardar preferencias de tema (claro/oscuro)

No utilizamos cookies de rastreo ni servicios de análisis de terceros.`
        },
        {
          title: '8. Cambios en esta Política',
          content: `Nos reservamos el derecho de modificar esta política de privacidad en cualquier momento. Los cambios serán efectivos inmediatamente después de su publicación en este sitio web.`
        },
        {
          title: '9. Contacto',
          content: `Si tiene preguntas sobre esta política de privacidad, puede contactarnos a través de los canales oficiales del proyecto.`
        }
      ]
    },
    en: {
      title: 'Privacy Policy',
      lastUpdate: 'Last updated: January 2025',
      sections: [
        {
          title: '1. General Information',
          content: `W2USDT is a token (smart contract) deployed on the Uni2 blockchain network (Chain ID: 111888). This bridge service allows users to convert their W2USDT tokens to USDT on the Binance Smart Chain (BSC) network.

By using this service, you agree to the practices described in this privacy policy.`
        },
        {
          title: '2. Nature of the Service',
          content: `W2USDT Bridge is a decentralized service that operates through smart contracts on the blockchain. The W2USDT contract is deployed on the Uni2 network and its code is public and verifiable.

We are not a regulated financial entity nor do we offer asset custody services. Users maintain full control of their funds at all times until they decide to perform a bridge operation.`
        },
        {
          title: '3. Data We Collect',
          content: `Due to the public nature of the blockchain, the following information is inherently public:

• Wallet addresses used for transactions
• Transaction amounts
• Transaction history on the blockchain
• Transaction hashes (TX Hash)

We do not collect personally identifiable information such as names, emails, phone numbers, or identity documents.`
        },
        {
          title: '4. Use of Information',
          content: `Blockchain transaction information is used exclusively for:

• Processing bridge requests
• Verifying transaction validity
• Maintaining a record of operations performed
• Improving service functionality`
        },
        {
          title: '5. Data Storage',
          content: `Transactions are permanently recorded on the Uni2 and BSC blockchains due to their immutable nature. Our systems maintain temporary records necessary for processing bridge requests.`
        },
        {
          title: '6. Security',
          content: `We implement technical security measures to protect the service, including:

• Audited smart contracts
• Transaction verification before processing
• Continuous system monitoring

However, users are responsible for the security of their own wallets and private keys.`
        },
        {
          title: '7. Cookies and Local Storage',
          content: `We use browser local storage (localStorage) only for:

• Saving language preferences
• Saving theme preferences (light/dark)

We do not use tracking cookies or third-party analytics services.`
        },
        {
          title: '8. Changes to this Policy',
          content: `We reserve the right to modify this privacy policy at any time. Changes will be effective immediately upon publication on this website.`
        },
        {
          title: '9. Contact',
          content: `If you have questions about this privacy policy, you can contact us through the official project channels.`
        }
      ]
    }
  }

  const t = content[language]

  return (
    <div className="max-w-4xl mx-auto py-8">
      <div className={cn(
        'rounded-2xl border p-8 md:p-12',
        theme === 'dark'
          ? 'bg-dark-900/60 border-dark-800'
          : 'bg-white border-gray-200'
      )}>
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-uni-500 to-uni-600 flex items-center justify-center shadow-glow-sm">
            <Shield className="w-7 h-7 text-dark-950" />
          </div>
          <div>
            <h1 className={cn(
              'text-3xl font-display font-bold',
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            )}>
              {t.title}
            </h1>
            <p className={cn('text-sm', theme === 'dark' ? 'text-dark-400' : 'text-gray-500')}>
              {t.lastUpdate}
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="space-y-8">
          {t.sections.map((section, i) => (
            <div key={i}>
              <h2 className={cn(
                'text-xl font-display font-semibold mb-4',
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              )}>
                {section.title}
              </h2>
              <div className={cn(
                'whitespace-pre-line leading-relaxed',
                theme === 'dark' ? 'text-dark-300' : 'text-gray-600'
              )}>
                {section.content}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
